// File: `typescript/apps/front-end/src/main/react/group/GroupButtons.tsx`
import React, {useState} from "react";
import {AjaxGroupRestControllerApi} from "@/generated-client";
import { isHandledAuthSessionError } from "@/main/common/AjaxAuthSessionHandler";
import { importChunkWithRetryAndReload } from "@/lib/lazyWithRetry";
import {ClientFactory} from "@/main/common/ClientFactory";
import { GroupGovernanceApi, type GroupInvitationAccessLevel, type GroupLeadershipLevel, type ViewerGroupInvitationSummary } from "@/main/common/GroupGovernanceApi";
import { isInvitationNoLongerActionableCode } from "@/main/common/GroupInvitationErrorCodes";
import {MetaHandler} from "@/main/common/MetaHandler";
import {shareUrl} from "@/main/util/PlatformIdentifierUtil";
import { showGroupLeadershipActionDialog } from "@/main/react/Dialog/GroupLeadershipActionDialog";
import {showReportEntityDialog} from "@/main/react/Dialog/ReportEntityDialog";
import {showUpdateGroupSettingsDialog} from "@/main/react/Dialog/UpdateGroupSettingsDialog";
import {
    emitGroupInvitationAdded,
    emitGroupInvitationRemoved,
    emitGroupJoinRequestSubmitted,
    emitGroupMemberAdded,
    onGroupInvitationAdded,
    onGroupInvitationRemoved,
    onGroupJoinRequestRemoved,
    onGroupJoinRequestSubmitted,
    onGroupMemberAdded,
    onGroupMemberRemoved,
} from "@/main/common/EventRegistry";
import {useViewerRelationshipState} from "@/main/common/ViewerActionRecords";
import {Localizer} from "@/main/react/Types";
import {cn} from "@/lib/utils";
import {Edit, Flag, Link2, MoreHorizontal, PlusSquare, Share, ShieldPlus, UserCheck2, UserMinus, UserPlus, UserX, X} from "lucide-react";
import { showYesNoDialog } from "@/main/react/Dialog/YesNoDialog";
import {CtaButton, CtaButtonDropdown, CtaToggle, DropdownItem, DropdownSection} from "@/main/react/buttons/CtaButton";
import GroupNotificationLevelControl from "@/main/react/group/GroupNotificationLevelControl";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {toast} from "sonner";
import {getSafeToastMessageFromError, getServerErrorCode} from "@/main/common/ApiUtils";
import logger from "@/lib/logger";
import {trackGroupJoin, trackGroupJoinRequested, trackGroupJoinStarted, trackShare, type AnalyticsSurface} from "@/main/analytics/analyticsEvents";
import {ACCOUNT_ACCESS_DIALOG_COPY, withAccountAccessSurface} from "@/main/react/account_access/AccountAccessDialogContext";
import {buildGroupJoinContinuationUrl, consumeGroupJoinIntent, consumeGroupWelcomeIntent} from "@/main/react/group/groupJoinIntent";
import type {GroupWelcomeContent} from "@/main/react/group/GroupWelcomeDialog";
import {maybeOpenGroupWelcomeDialog, openGroupAboutDialog} from "@/main/react/group/openGroupWelcomeDialog";
import {Info} from "lucide-react";
import {buildGroupListMenuItems} from "@/main/react/group/lists/groupListMenuItems";
import {ensureGroupLists, useGroupLists} from "@/main/react/group/lists/groupListsQuery";
import type { GroupDiscussionSubmissionPolicyValue } from "@/main/react/group/GroupDiscussionSubmissionPolicy";
import { completeGroupLeave, openGroupLeaderLeaveDialog } from "@/main/react/group/leave/groupLeave";
import { openGroupInviteLinkPanel } from "@/main/react/group/invite/openGroupInviteLinkPanel";

/**
 * Can this viewer invite people into this group?
 *
 * <p>Exported because the answer is a **product** rule, not a rendering detail: in a public group any
 * member (or leader) may invite; in a private group only leaders at MODERATOR or above may. That
 * mirrors `GroupService.inviteUser` on the backend, and a second copy of it somewhere else on the group
 * page is how the two come to disagree. `EMPTY-STATES-47` needed exactly this predicate on the empty
 * discussions state, so it reads it from here rather than re-deriving it.
 */
export type GroupInviteAffordance = "invite" | "request" | "none";

/**
 * The single source for BOTH invite questions, because they are one product rule with two outcomes.
 *
 * <p>Added 2026-09-02 alongside member invite requests. `canViewerInviteToGroup` below is now a thin
 * reading of this, so the two can never disagree — which is the property the old comment was asking
 * for and the reason this was not written as a second predicate beside it.
 *
 * <ul>
 *   <li><b>invite</b> — the viewer may send an invitation outright. Public group + member, or private
 *       group + MODERATOR or above. Unchanged from before.</li>
 *   <li><b>request</b> — the viewer may ask a leader to send one. Private group + ordinary member.
 *       This is the case that used to be `none`, and it is the whole feature: a member's vouching had
 *       nowhere to go, so a private group's members could bring nobody in.</li>
 *   <li><b>none</b> — not a member, or banned.</li>
 * </ul>
 *
 * <p>Mirrors `GroupService.assertCanSendInvitation` and `GroupInviteRequestService.requestInvite`. ⚠️ A
 * second copy of this rule anywhere on the group page is how the two come to disagree; `EMPTY-STATES-47`
 * and the person-first picker both read it from here.
 *
 * <p>Design: `designs/01-now/2026-09-02-member-invite-request-with-leader-preapproval-design.md`.
 */
export function resolveGroupInviteAffordance(args: {
    privateAccess?: boolean;
    isMember?: boolean;
    isLeader?: boolean;
    viewerLeadershipLevel?: GroupLeadershipLevel | null;
}): GroupInviteAffordance {
    const { privateAccess, isMember, isLeader, viewerLeadershipLevel } = args;
    const isAtLeastModerator = viewerLeadershipLevel === "OWNER"
        || viewerLeadershipLevel === "ADMINISTRATOR"
        || viewerLeadershipLevel === "MODERATOR";
    const belongs = Boolean(isMember) || Boolean(isLeader);

    if (!privateAccess) {
        return belongs ? "invite" : "none";
    }
    if (isAtLeastModerator) {
        return "invite";
    }
    return belongs ? "request" : "none";
}

export function canViewerInviteToGroup(args: {
    privateAccess?: boolean;
    isMember?: boolean;
    isLeader?: boolean;
    viewerLeadershipLevel?: GroupLeadershipLevel | null;
}): boolean {
    return resolveGroupInviteAffordance(args) === "invite";
}

/**
 * Open the invite-by-handle dialog for a group.
 *
 * <p>Lifted out of `GroupButtons` for `EMPTY-STATES-47`: the growth affordances all lived inside the
 * unlabelled overflow, so an empty group's page had no visible way to bring anyone in. The empty state
 * now offers this one, and it calls the same function the overflow item does — the access-level
 * resolution below mirrors the backend's `canInvite` rules and must not exist twice.
 */
export function showGroupInviteUserDialog(args: {
    groupPublicId: string;
    groupName?: string;
    viewerLeadershipLevel?: GroupLeadershipLevel | null;
    invitedByUsername?: string;
    localizer: Localizer;
}) {
    const { groupPublicId, groupName, viewerLeadershipLevel, invitedByUsername, localizer } = args;

    // Only offer access levels the viewer may actually grant: owners can grant any level,
    // administrators can grant moderator/member, and moderators or plain members can only
    // invite at member level (this mirrors the backend's canInvite rules).
    const allowedAccessLevels: GroupInvitationAccessLevel[] =
        viewerLeadershipLevel === "OWNER"
            ? ["MEMBER", "MODERATOR", "ADMINISTRATOR"]
            : viewerLeadershipLevel === "ADMINISTRATOR"
                ? ["MEMBER", "MODERATOR"]
                : ["MEMBER"];

    showGroupLeadershipActionDialog({
        localizer,
        title: localizer("group.invitations.invite_user"),
        confirmLabel: localizer("group.invitations.send"),
        initialAccessLevel: "MEMBER",
        allowedAccessLevels,
        /**
         * Branch on the RESULT, never on "the request did not throw".
         *
         * <p>Until 2026-08-20 this emitted `emitGroupInvitationAdded` and toasted "Invitation sent."
         * on any 2xx. Stage 1 of the invite-a-person slice then converted three formerly-thrown cases
         * — already a member, already invited, and a decline still inside the cooldown — into ordinary
         * 200s carrying a status, so the panel started claiming success and painting a phantom pending
         * row for all three (design §6.2). The switch lives HERE, in the shared helper, rather than in
         * one caller: extracting this dialog gave it a second call site on the group page, and a fix
         * that sits in a caller is a fix the next call site does not get.</p>
         */
        onConfirm: async ({ username, accessLevel }) => {
            const status = await GroupGovernanceApi.inviteUser(
                groupPublicId, username, accessLevel as GroupInvitationAccessLevel);

            if (status === "ALREADY_IN_GROUP") {
                toast.info(localizer("group.invitations.already_member"));
                return;
            }
            if (status === "ALREADY_INVITED") {
                // Deliberately the same answer for a pending invitation and for a decline inside the
                // cooldown: whether someone declined your group is their business, not the group's
                // (design §9.2).
                toast.info(localizer("group.invitations.already_invited"));
                return;
            }
            if (status === "ADDED") {
                // They had asked to join; the invite approved that request and they are a member NOW.
                // No invitation row exists, so emitting one would paint a pending entry for a person
                // who is already in the roster (design §7).
                emitGroupMemberAdded({
                    groupPublicId,
                    member: { username, groupPublicId },
                    source: "requestApproved",
                });
                toast.success(groupName
                    ? localizer("user.invite_to_group.success.added", username, groupName)
                    : localizer("group.invitations.send_success"));
                return;
            }

            emitGroupInvitationAdded({
                groupPublicId,
                username,
                invitation: {
                    username,
                    invitedByUsername,
                    leadershipLevel: accessLevel === "MEMBER" ? null : accessLevel,
                },
            });
            toast.success(localizer("group.invitations.send_success"));
        },
    });
}

/**
 * Open the "ask a leader to invite this person" dialog, for an ordinary member of a PRIVATE group.
 *
 * <p>Deliberately a sibling of {@link showGroupInviteUserDialog} rather than a mode inside it: the two
 * produce different objects with different privacy rules. An invite is a pre-authorization that reaches
 * the nominee immediately; a request reaches only the group's leaders and the nominee learns nothing
 * unless it is approved. Folding them together is how one set of copy ends up describing the other.
 *
 * <p>⚠️ NO access-level picker. A member cannot ask that somebody be made a leader — the backend refuses
 * it, and `LeadershipLevel.canInvite` returns false for MODERATOR anyway, so offering the control would
 * be offering an action the server declines (rule 23).
 *
 * <p>Design: `designs/01-now/2026-09-02-member-invite-request-with-leader-preapproval-design.md`.
 */
export function showGroupInviteRequestDialog(args: {
    groupPublicId: string;
    localizer: Localizer;
}) {
    const { groupPublicId, localizer } = args;

    showGroupLeadershipActionDialog({
        localizer,
        title: localizer("group.invite_request.request_invite"),
        confirmLabel: localizer("group.invite_request.send"),
        initialAccessLevel: "MEMBER",
        allowedAccessLevels: ["MEMBER"],
        // Branch on the RESULT, never on "it did not throw" — the same rule the invite dialog above
        // records, and for the same reason: three of the four outcomes are ordinary 200s.
        onConfirm: async ({ username }) => {
            const status = await GroupGovernanceApi.createInviteRequest(groupPublicId, username);

            if (status === "ALREADY_IN_GROUP") {
                toast.info(localizer("group.invitations.already_member"));
                return;
            }
            if (status === "ALREADY_INVITED") {
                toast.info(localizer("group.invitations.already_invited"));
                return;
            }
            if (status === "ALREADY_REQUESTED") {
                toast.info(localizer("group.invite_request.already_requested"));
                return;
            }
            // ⚠️ No `emitGroupInvitationAdded` here, and that is the point rather than an omission:
            // nothing pending exists for the nominee yet, so painting an invitation row would show a
            // pre-authorization that no leader has granted.
            toast.success(localizer("group.invite_request.success"));
        },
    });
}

type GroupButtonsProps = {
    groupPublicId: string;
    groupSlug: string;
    initiallyMember?: boolean;
    initiallyLeader?: boolean;
    /**
     * The viewer's leadership level. Edit-settings and invite are only available to OWNER /
     * ADMINISTRATOR; a MODERATOR is still `initiallyLeader` (for moderation sections) but must not
     * see admin-only affordances, because the backend rejects those actions for moderators.
     */
    viewerLeadershipLevel?: GroupLeadershipLevel;
    initiallyBanned?: boolean;
    initiallyRequestedToJoin?: boolean;
    groupName?: string;
    groupDescription?: string;
    groupThumbnailUrl?: string;
    mainLocale?: string;
    discussionSubmissionPolicy?: GroupDiscussionSubmissionPolicyValue;
    privateAccess?: boolean;
    sensitive?: boolean;
    compact?: boolean;
    displayMode?: "full" | "primary-only";
    /**
     * Lay the action row out from the leading edge instead of the trailing one.
     *
     * <p>Opt-in rather than derived from `compact`, because the feed cards and the discovery rail
     * genuinely want a trailing cluster. On the group page's own header every other row is flush to
     * the content left edge, so a trailing cluster is the one misaligned element in the card — and
     * the desktop rail header's `justify-start` wrapper was dead code, overridden by this
     * component's own `ml-auto`.
     */
    alignStart?: boolean;
    /**
     * Give the primary action the row's full width (`compact` + `alignStart` only).
     *
     * <p>On a phone the group's primary action was ~19% of the card width, weighted the same as the
     * two icon controls beside it, while being the one action the surface exists to offer.
     */
    stretchPrimary?: boolean;
    /**
     * Render the primary action in a quieter tier (`outline` rather than `primary`).
     *
     * <p>Presentational, and in the same family as `alignStart` and `stretchPrimary` above: the HOST
     * decides, this component never infers it. `DASHBOARD-12` measured eleven identical filled-blue
     * "Add post" buttons down a self-view membership list, each the highest-contrast element in its
     * row, while the action the list exists for — opening the group — had no visible control at all.
     *
     * <p><b>Why it is a prop and not a rule in here.</b> `GroupButtons` also mounts on the directory,
     * the home rails and the group page's own header, where a loud primary action is deliberate. This
     * component cannot tell those surfaces apart, and the analytics `surface` prop is not a
     * presentation signal — re-purposing it would be the same mistake spelled differently. So the
     * card passes it, and the default is unchanged.
     *
     * <p><b>`DISCOVERY-74`, 2026-09-02 — it now demotes the JOIN tier too, not only "Add post".</b>
     * `GroupCardDisplayer` sets it for a signed-out viewer, for whom the card's primary action is
     * READ: `Join` is `requiresLogin`, so a filled primary repeated down a directory is twenty copies
     * of a signup wall above the one control the visitor can actually complete. The extension is a
     * no-op for the caller that existed before — the dashboard self-view list, whose viewer is a
     * member, where `joinVariant` already resolved to `outline`. Still a PRESENTATION and never a
     * permission (design language rule 6): `Join` stays rendered, enabled, and opens the same
     * account-access dialog.
     */
    demotePrimaryAction?: boolean;
    /**
     * Collapse the primary action to its icon below `sm`, the way the card's open-group control
     * already does (`DASHBOARD-62`).
     *
     * <p>Same family as `alignStart` / `stretchPrimary` / `demotePrimaryAction` above, and a prop for
     * the same reason: this component also mounts on the group page's own header, where a labelled
     * primary action is right, and it cannot tell those surfaces apart.
     *
     * <p><b>Why the card asks for it.</b> `DASHBOARD-12` made the card's open-group control icon-only
     * below `sm` precisely so a long locale could not wrap the footer, as it had on the series card
     * (`DASHBOARD-36`) — but "Add post" kept its label, so the slack simply moved to the other button.
     * Measured across all 25 catalogs: `fr` is "Ajouter une publication" at 25 characters against
     * `es`'s 21, and the Spanish footer already clears the 412px card by about five pixels. One
     * collapse rule for both labels is the first of the two fixes the row offers, and the one its own
     * cross-references already support.
     *
     * <p>⚠️ A collapsed label MUST be paired with an `ariaLabel`, and nothing in the build will tell
     * you otherwise: `displayTextOnMobile={false}` resolves to `hidden sm:inline`, and `display: none`
     * removes the text from the ACCESSIBLE NAME computation rather than merely from the layout —
     * `DISCOVERY-33`, whose enforcement gap in `warnIfCtaHasNoAccessibleName` was re-confirmed on
     * 2026-08-25 (wave-15 prerequisite P3). The `ariaLabel` below is the same key as the visible text,
     * so a sighted and a non-sighted member describe the same control.
     */
    collapsePrimaryLabelOnMobile?: boolean;
    /**
     * The tier of `displayMode="primary-only"`'s Join: `outline` (default, the rail/overlay presentation
     * operator decision B12 reserved) or `primary`. Round 38 D31 (`EMPTY-STATES-112`): the group's own
     * empty Posts panel mounts this component as the panel's ONE action for a joinable non-member, where
     * Join is the primary by decision. Presentation only, like `demotePrimaryAction`; the host decides.
     */
    primaryOnlyTone?: "outline" | "primary";
    /**
     * Where `displayMode="primary-only"` places its action: `end` (default, every rail) or `center`.
     * S7 A16 (`EMPTY-STATES-112`): inside the empty Posts panel's centred column the root is only as
     * wide as the line above it, so `justify-end` hung the Join pill right of the centre line.
     */
    primaryOnlyAlign?: "end" | "center";
    /**
     * Overrides the Join control's `data-playwright-id` (default `group-join-toggle`). For a second
     * mount on a page whose header already carries the default hook, so specs resolving the header's
     * control never meet two visible matches (round 38 D31: `group-discussions-empty-join`).
     */
    joinPlaywrightId?: string;
    /** Where this button is mounted, for join/leave attribution. See the join/follow analytics design. */
    surface?: AnalyticsSurface;
    /**
     * Content for the new-joiner welcome dialog and the "About this group" overflow entry. Supplied
     * only where it is actually available — the group page, which already holds the governance
     * bootstrap. Absent everywhere else (feed cards, directory), which simply means those mounts
     * greet nobody and show no About entry. See
     * designs/01-now/2026-07-21-group-new-joiner-welcome-technical-design.md.
     */
    welcomeContent?: GroupWelcomeContent;
    localizer: Localizer;
};

export default function GroupButtons(props: GroupButtonsProps) {
    const {
        groupPublicId,
        initiallyMember = false,
        initiallyLeader = false,
        viewerLeadershipLevel,
        initiallyBanned = false,
        initiallyRequestedToJoin = false,
        groupName = "",
        groupDescription = "",
        groupThumbnailUrl = "",
        mainLocale,
        discussionSubmissionPolicy,
        privateAccess = false,
        sensitive = false,
        compact = false,
        collapsePrimaryLabelOnMobile = false,
        displayMode = "full",
        alignStart = false,
        stretchPrimary = false,
        demotePrimaryAction = false,
        primaryOnlyTone = "outline",
        primaryOnlyAlign = "end",
        joinPlaywrightId = "group-join-toggle",
        surface = "page",
        welcomeContent,
        localizer,
    } = props;

    const apiClient: AjaxGroupRestControllerApi = ClientFactory.getAjaxGroupRestControllerApi();

    // Not plain useState: discovery cards unmount when their section collapses, and the `initially*`
    // props are stale page-bootstrap values on remount. The hook also owns the prop resync that used to
    // live in the effect below, so a re-supplied stale prop can no longer clobber a real join.
    const [isMember, setIsMember] = useViewerRelationshipState("group-membership", groupPublicId, initiallyMember);
    const [busy, setBusy] = useState(false);

    const [isBanned, setIsBanned] = useState<boolean>(initiallyBanned);
    /*
     * SV-144 (A14 as amended by Q2 = B): a PARTICIPANT (member OR leader, not banned, signed in) can
     * favorite the group and put it in lists from this overflow, on the group page and on every card.
     * The observer never fetches by itself: a feed of cards costs nothing until a member opens a menu,
     * which loads the one shared cache (`groupListsQuery`).
     */
    const { lists: groupLists } = useGroupLists(false);

    const [isRequestSent, setIsRequestSent] = useViewerRelationshipState("group-join-request", groupPublicId, initiallyRequestedToJoin);
    const [isRequesting, setIsRequesting] = useState<boolean>(false);
    const [viewerInvitation, setViewerInvitation] = useState<ViewerGroupInvitationSummary | null>(null);
    const [invitationBusy, setInvitationBusy] = useState(false);
    const loggedInUsername = MetaHandler.getLoggedInUsername();
    const accountAreaAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.accountArea, "group_account_area");
    const groupMembershipAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.groupMembership, "group_membership");
    // Join/request gating carries a continuation back to this group's own page (`?join=1`) so the
    // intended join resumes automatically once the visitor authenticates (login path immediately;
    // signup path via the verification-email link). See groupJoinIntent.ts + the 2026-06-20
    // group-join-resume design. Leave/accept/decline keep the plain dialog (they are logged-in already).
    const groupJoinAccountAccessDialog = {
        ...groupMembershipAccountAccessDialog,
        continuation: { kind: "url" as const, url: buildGroupJoinContinuationUrl(props.groupSlug) },
    };
    const addToGroupAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.addToGroup, "group_add_discussion");
    const reportAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.report, "group_report");

    React.useEffect(() => {
        setIsBanned(Boolean(initiallyBanned));
    }, [groupPublicId, initiallyBanned]);

    React.useEffect(() => {
        setBusy(false);
        setIsRequesting(false);
        setInvitationBusy(false);
        setViewerInvitation(null);
    }, [groupPublicId]);

    const loadViewerInvitation = React.useCallback(() => {
        if (!MetaHandler.isUserLoggedIn() || isMember || isBanned || isRequestSent || !groupPublicId) {
            setViewerInvitation(null);
            return Promise.resolve();
        }

        return GroupGovernanceApi.getViewerPendingInvitation(groupPublicId)
            .then((invitation) => {
                setViewerInvitation(invitation);
            })
            .catch((error) => {
                logger.warn("Failed loading viewer invitation", error);
            });
    }, [groupPublicId, isBanned, isMember, isRequestSent]);

    React.useEffect(() => {
        // No cleanup flag here on purpose: the previous one guarded an empty branch, so it cancelled
        // nothing while reading as protection. `loadViewerInvitation` already swallows its own failure
        // and a late setState on an unmounted component is a no-op in React 18+.
        void loadViewerInvitation();
    }, [loadViewerInvitation]);

    React.useEffect(() => {
        if (!groupPublicId) {
            return;
        }

        const unsubscribeMemberAdded = onGroupMemberAdded((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId) {
                return;
            }

            const memberUsername = payload.member?.username?.trim();

            if (memberUsername && loggedInUsername && memberUsername === loggedInUsername) {
                setIsMember(true);
                setIsRequestSent(false);
                setViewerInvitation(null);
            }
        });

        const unsubscribeMemberRemoved = onGroupMemberRemoved((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId) {
                return;
            }

            const removedUsername = payload.username?.trim();

            if (removedUsername && loggedInUsername && removedUsername === loggedInUsername) {
                setIsMember(false);
            }
        });

        const unsubscribeRequestSubmitted = onGroupJoinRequestSubmitted((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId) {
                return;
            }

            const requestUsername = payload.request?.username?.trim() ?? payload.username?.trim();
            if (requestUsername && loggedInUsername && requestUsername === loggedInUsername) {
                setIsRequestSent(true);
                setViewerInvitation(null);
            }
        });

        const unsubscribeRequestRemoved = onGroupJoinRequestRemoved((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId) {
                return;
            }

            const requestUsername = payload.username?.trim();
            if (requestUsername && loggedInUsername && requestUsername === loggedInUsername) {
                setIsRequestSent(false);
            }
        });

        const unsubscribeInvitationAdded = onGroupInvitationAdded((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId) {
                return;
            }

            const invitedUsername = payload.invitation?.username?.trim() ?? payload.username?.trim();
            if (invitedUsername && loggedInUsername && invitedUsername === loggedInUsername) {
                void loadViewerInvitation();
            }
        });

        const unsubscribeInvitationRemoved = onGroupInvitationRemoved((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId) {
                return;
            }

            if (viewerInvitation?.invitationPublicId && payload.invitationPublicId === viewerInvitation.invitationPublicId) {
                setViewerInvitation(null);
                return;
            }

            const invitedUsername = payload.username?.trim();
            if (invitedUsername && loggedInUsername && invitedUsername === loggedInUsername) {
                setViewerInvitation(null);
            }
        });

        return () => {
            unsubscribeMemberAdded();
            unsubscribeMemberRemoved();
            unsubscribeRequestSubmitted();
            unsubscribeRequestRemoved();
            unsubscribeInvitationAdded();
            unsubscribeInvitationRemoved();
        };
    }, [groupPublicId, loadViewerInvitation, loggedInUsername, viewerInvitation?.invitationPublicId]);

    // Leaders cannot leave the group while they remain leaders (backend enforces this).
    const cannotLeaveBecauseLeader = initiallyLeader && isMember;
    // P1-B: an owner's way out is transfer-then-step-down, so name that first step instead of leaving
    // them to discover it. Other leaders just need to drop their own leadership.
    const cannotLeaveReasonKey = viewerLeadershipLevel === "OWNER"
        ? "group.ownership.leave_hint"
        : "group.leader.cannot_leave";

    const getStatus = (err: any) => err?.response?.status ?? err?.status;
    const getServerMessage = (err: any) => err?.response?.data?.message ?? err?.message;

    const toggleMembership = async (surfaceOverride?: AnalyticsSurface, leaveConfirmed = false) => {
        if (busy) return;
        if (isBanned) {
            return;
        }
        if (cannotLeaveBecauseLeader) {
            return;
        }

        // D1 / GROUP-49 (QoL slice `1j`): leaving asks first, on every surface that renders these
        // buttons (group page header and menu, directory/dashboard/profile cards), reusing the group
        // chat leave dialog's title and action so the two paths cannot disagree. Undo was rejected:
        // leaving deletes the member row (flair, notification level) and a private rejoin needs a
        // leader. Joining stays one tap.
        if (isMember && !leaveConfirmed) {
            showYesNoDialog({
                localizer,
                title: localizer("chat.group.leave.confirm_title"),
                message: <div>{localizer(privateAccess ? "group.leave.confirm_body.private" : "group.leave.confirm_body.public")}</div>,
                yesLabel: localizer("chat.group.leave.action"),
                yesIcon: UserMinus,
                noLabel: localizer("literal.cancel"),
                noIcon: X,
                destructive: true,
                yesPlaywrightId: "group-leave-confirm",
                noPlaywrightId: "group-leave-cancel",
                onYes: () => toggleMembership(surfaceOverride, true),
            });
            return;
        }

        const eventSurface = surfaceOverride ?? surface;
        setBusy(true);
        try {
            if (isMember) {
                // The ONE leave in the product (§F17): the room menu calls the same function.
                await completeGroupLeave({ groupPublicId, surface: eventSurface, localizer });
            } else {
                // Intent event fires before the mutation so click->success conversion is measurable.
                trackGroupJoinStarted(eventSurface);
                await apiClient.joinGroup(groupPublicId, eventSurface);
                if (privateAccess) {
                    emitGroupJoinRequestSubmitted({
                        groupPublicId,
                        username: loggedInUsername ?? undefined,
                        request: loggedInUsername ? {
                            username: loggedInUsername,
                            requestStatus: "PENDING",
                        } : undefined,
                    });
                    trackGroupJoinRequested(eventSurface);
                    toast.success(localizer("group.request_sent_confirmation"));
                } else {
                    emitGroupMemberAdded({
                        groupPublicId,
                        member: {
                            username: loggedInUsername ?? undefined,
                            groupPublicId,
                        },
                            source: "join",
                    });
                    trackGroupJoin(eventSurface);
                    toast.success(localizer("group.membership.joined"));
                    // The greeting arrives on the next beat, over an already-updated page: the button
                    // has flipped to Joined and the member count has ticked up, so the join itself
                    // still feels instant. No-ops when the owner authored nothing (UX §6).
                    if (welcomeContent) {
                        maybeOpenGroupWelcomeDialog({ ...welcomeContent, groupPublicId }, localizer);
                    }
                }
            }
        } catch (err) {
            if (isHandledAuthSessionError(err)) {
                return;
            }
            const status = getStatus(err);

            // 400 = bad input, 403 = not allowed (banned / not a member), 409 = state conflict.
            // All carry a localized server reason worth surfacing verbatim. 401 is already handled
            // above by the auth-session layer (isHandledAuthSessionError).
            if (status === 400 || status === 403 || status === 409) {
                const message = getServerMessage(err) || localizer("group.request_failed");
                toast.error(message);
                return;
            }

            logger.error("Group membership toggle failed", err);
            toast.error(localizer("group.membership.update_failed"));
        } finally {
            setBusy(false);
        }
    };

    const requestToJoin = async () => {
        if (isRequesting || busy) return;
        setIsRequesting(true);

        try {
            // Intent event fires before the mutation so click->success conversion is measurable.
            trackGroupJoinStarted(surface);
            await apiClient.joinGroup(groupPublicId, surface);
            emitGroupJoinRequestSubmitted({
                groupPublicId,
                username: loggedInUsername ?? undefined,
                request: loggedInUsername ? {
                    username: loggedInUsername,
                    requestStatus: "PENDING",
                } : undefined,
            });
            trackGroupJoinRequested(surface);
            toast.success(localizer("group.request_sent_confirmation"));
        } catch (err) {
            if (isHandledAuthSessionError(err)) {
                return;
            }
            const status = getStatus(err);
            if (status === 400 || status === 403 || status === 409) {
                const message = getServerMessage(err) || localizer("group.request_failed");
                toast.error(message);
                return;
            }
            logger.error("Request to join failed", err);
            toast.error(localizer("group.request.update_failed"));
        } finally {
            setIsRequesting(false);
        }
    };

    // Resume an intended join after the visitor authenticated elsewhere (login immediately, or signup
    // via the verification-email link): if this group's own page carries the `?join=1` continuation and
    // the now-logged-in viewer is eligible, auto-complete it. toggleMembership handles public join +
    // private request; the join stays fully authorization-checked server-side. Runs once on mount.
    const joinIntentHandledRef = React.useRef(false);
    React.useEffect(() => {
        if (joinIntentHandledRef.current || !MetaHandler.isUserLoggedIn()) {
            return;
        }
        if (!consumeGroupJoinIntent(props.groupSlug)) {
            return;
        }
        joinIntentHandledRef.current = true;
        if (isMember || isBanned || isRequestSent || cannotLeaveBecauseLeader) {
            return;
        }
        void toggleMembership("deep_link_resume");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Private-group trigger: arriving from the group-approval notification (`?welcome=1`) means the
    // user is here *because* they were just let in, which is the one moment worth greeting them. That
    // one-time event is what lets the greeting exist without any stored "was this person welcomed?"
    // state — see the technical design §4b. Runs once on mount, and only for an actual member.
    const welcomeIntentHandledRef = React.useRef(false);
    React.useEffect(() => {
        if (welcomeIntentHandledRef.current || !MetaHandler.isUserLoggedIn()) {
            return;
        }
        if (!consumeGroupWelcomeIntent(props.groupSlug, groupPublicId)) {
            return;
        }
        welcomeIntentHandledRef.current = true;
        if (!isMember || !welcomeContent) {
            return;
        }
        // GROUP-95 (A18): a leader is a member too, so without this the author of the greeting was
        // welcomed to their own group by their own words. A leader falls through to the "about" mode,
        // the existing re-read idiom; only an ordinary member gets the join greeting.
        if (initiallyLeader) {
            openGroupAboutDialog(welcomeContent, localizer);
            return;
        }
        maybeOpenGroupWelcomeDialog({ ...welcomeContent, groupPublicId }, localizer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onAddDiscussionToGroup = (ev?: Event) => {
        ev?.preventDefault();
        ev?.stopPropagation();
        // Code-split: GroupAddDiscussionToGroupDialog is 47.7 KB and was on the EAGER startup graph
        // through this static import -- GroupButtons is reached from bootstrapFrontEnd via
        // OnboardingWelcomeRuntime -> openWelcomeOverlay -> WelcomeOverlay, so every page paid for a
        // picker that only opens on this click. Rule 26: routed through the retry helper, never a bare
        // import(). Imperative open, so importChunkWithRetryAndReload rather than lazyWithRetry.
        // The `{ default: m }` wrap is the established idiom for a named export here (see
        // bodyClipPlayers.ts and PlatformIdentifierUtil.ts): the helper is typed for default exports.
        void importChunkWithRetryAndReload(
            () => import("@/main/react/Dialog/GroupAddDiscussionToGroupDialog").then((m) => ({ default: m })),
            "group-add-discussion-dialog",
        ).then((module) => module.default.showAddDiscussionsToGroupDialog(groupPublicId, localizer));
    };

    const onShareGroup = async (ev?: Event) => {
        try {
            ev?.preventDefault();
            ev?.stopPropagation();
            trackShare("group", "group_actions");
            const sharePath = `/group/${props.groupSlug}`;
            const shareFull = (typeof window !== "undefined" ? window.location.origin : "") + sharePath;
            await shareUrl(shareFull, localizer("literal.share"), localizer,
                { entityType: "GROUP", entityId: groupPublicId, entityTitle: groupName });
        } catch (err) {
            logger.error("Share failed", err);
            toast.error(localizer("group.share.failed"));
        }
    };

    /**
     * The group invite link (Phase D of the referral design, §5A).
     *
     * Identical mechanics to `onShareGroup` on purpose — same URL, same `shareUrl()` chokepoint, so it
     * inherits the `?sv_s` token, the `SHARE_INITIATED` row with the sharer's `user_id`, the
     * native/drawer/clipboard branching and the recipient-directed payload for free. Building a second
     * path would have got none of that. The ONE difference is `moment: "group_invite"`, which is what
     * makes a deliberate invite separable in SQL from the ambient Share item in this same menu:
     * `from_page` normalizes both to `/group/*`, so without the moment they are one row.
     *
     * It is a deep link plus attribution, never a capability grant — which is why it is offered on
     * PUBLIC groups only (see `canInviteWithLink`). The link grants nothing the URL did not already
     * grant, and the landing page is the ordinary public group page a logged-out stranger can already
     * read end to end.
     */
    const onInviteWithLink = async (ev?: Event) => {
        try {
            ev?.preventDefault();
            ev?.stopPropagation();
            trackShare("group", "group_actions");
            const sharePath = `/group/${props.groupSlug}`;
            const shareFull = (typeof window !== "undefined" ? window.location.origin : "") + sharePath;
            await shareUrl(shareFull, localizer("group.invite_link.action"), localizer,
                { entityType: "GROUP", entityId: groupPublicId, entityTitle: groupName, moment: "group_invite" });
        } catch (err) {
            logger.error("Group invite link share failed", err);
            toast.error(localizer("group.share.failed"));
        }
    };

    const onInviteUser = () => showGroupInviteUserDialog({
        groupPublicId,
        groupName,
        viewerLeadershipLevel,
        invitedByUsername: loggedInUsername ?? undefined,
        localizer,
    });

    /**
     * The invitation was already gone when we acted on it: accepted, declined, or withdrawn by the group.
     *
     * <p>Not a failure, and reporting it as one is what keeps a dead control alive. This page had NO
     * coded branch at all until 2026-08-24 — only `toast.error` — so the whole recovery
     * `GroupInvitationNotificationDialog` has carried since 2026-08-11 stopped at the drawer's edge, and
     * the same stale invitation refused a member here for as long as they kept pressing.
     *
     * <p>The server's own localized text is reused deliberately: it already says the useful thing, in all
     * 25 locales, and it says a DIFFERENT useful thing for a withdrawn invitation than for one already
     * answered. Inventing a second string here would flatten that distinction back out.
     */
    const resolveInvitationAsAlreadyHandled = (invitationPublicId: string, error: unknown) => {
        emitGroupInvitationRemoved({
            groupPublicId,
            invitationPublicId,
            username: loggedInUsername ?? undefined,
            resolution: "resolved",
        });
        toast.info(getSafeToastMessageFromError(error, localizer("group.invitations.no_longer_available")));
    };

    const acceptInvitation = async () => {
        if (!viewerInvitation?.invitationPublicId || invitationBusy) return;
        const invitationPublicId = viewerInvitation.invitationPublicId;
        setInvitationBusy(true);
        try {
            await GroupGovernanceApi.acceptInvitation(invitationPublicId);
            emitGroupInvitationRemoved({
                groupPublicId,
                invitationPublicId,
                username: loggedInUsername ?? undefined,
                resolution: "accepted",
            });
            emitGroupMemberAdded({
                groupPublicId,
                member: {
                    username: loggedInUsername ?? undefined,
                    groupPublicId,
                },
                source: "invitationAccepted",
            });
            toast.success(localizer("group.invitations.accept_success"));
        } catch (error: any) {
            if (isInvitationNoLongerActionableCode(getServerErrorCode(error))) {
                resolveInvitationAsAlreadyHandled(invitationPublicId, error);
                return;
            }
            logger.error("Accept invitation failed", error);
            toast.error(getSafeToastMessageFromError(error, localizer("group.invitations.accept_failed")));
        } finally {
            setInvitationBusy(false);
        }
    };

    const declineInvitation = async () => {
        if (!viewerInvitation?.invitationPublicId || invitationBusy) return;
        const invitationPublicId = viewerInvitation.invitationPublicId;
        setInvitationBusy(true);
        try {
            await GroupGovernanceApi.declineInvitation(invitationPublicId);
            emitGroupInvitationRemoved({
                groupPublicId,
                invitationPublicId,
                username: loggedInUsername ?? undefined,
                resolution: "declined",
            });
            toast.success(localizer("group.invitations.decline_success"));
        } catch (error: any) {
            // Decline gets the same treatment, and for the same reason: the two controls sit side by
            // side, so a dead invitation that vanishes under Accept and errors under Decline would read
            // as one of them being broken.
            if (isInvitationNoLongerActionableCode(getServerErrorCode(error))) {
                resolveInvitationAsAlreadyHandled(invitationPublicId, error);
                return;
            }
            logger.error("Decline invitation failed", error);
            toast.error(getSafeToastMessageFromError(error, localizer("group.invitations.decline_failed")));
        } finally {
            setInvitationBusy(false);
        }
    };

    const otherItems: DropdownItem[] = [];
    const moderationItems: DropdownItem[] = [];

    // "About this group" — the permanent way back to the welcome content, for members and
    // non-members alike. For a non-member this is a genuine pre-join preview: the rules become
    // readable *before* deciding to join, rather than being rail furniture. Deliberately has no
    // suppression and no login requirement (UX §7).
    if (welcomeContent) {
        otherItems.push({
            id: "about-group",
            icon: Info,
            // Explicitly open to anonymous visitors: the pre-join preview is the point.
            requiresLogin: false,
            text: localizer("group.about.title"),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                    // ignore
                }
                openGroupAboutDialog(welcomeContent, localizer);
            },
        });
    }

    // Edit settings and invite require ADMINISTRATOR or OWNER on the backend, so only show those
    // affordances at those levels. A MODERATOR is still a leader (for moderation sections) but must
    // not see actions they cannot complete. A missing level is treated as "not an administrator".
    // Editing settings requires ADMINISTRATOR or OWNER on the backend.
    const canAdministerGroup = viewerLeadershipLevel === "OWNER" || viewerLeadershipLevel === "ADMINISTRATOR";
    // Inviting is privacy-aware: in public groups any member (or leader) may invite people; in
    // private groups only leaders at MODERATOR or above may. This mirrors GroupService.inviteUser.
    // ONE derivation, shared with the group page's empty state (`EMPTY-STATES-47`) so the two cannot
    // drift apart from the backend rule they both mirror.
    const inviteAffordance = resolveGroupInviteAffordance({
        privateAccess,
        isMember,
        isLeader: Boolean(initiallyLeader),
        viewerLeadershipLevel,
    });
    const canInviteUsers = inviteAffordance === "invite";
    // The 2026-09-02 addition: an ordinary member of a private group can now ask a leader to invite
    // somebody, where before this branch was simply `false` and the member had no route at all.
    const canRequestInvite = inviteAffordance === "request";

    if (canAdministerGroup) {
        otherItems.push({
            id: "edit-group",
            icon: Edit,
            requiresLogin: true,
            accountAccessDialog: accountAreaAccountAccessDialog,
            text: localizer("group.settings.edit"),
            onClick: () => {
                showUpdateGroupSettingsDialog({
                    groupPublicId,
                    name: groupName,
                    description: groupDescription,
                    welcomeMessage: welcomeContent?.welcomeMessage ?? "",
                    thumbnailUrl: groupThumbnailUrl,
                    mainLocale,
                    discussionSubmissionPolicy,
                    privateAccess,
                    sensitive,
                    viewerIsOwner: viewerLeadershipLevel === "OWNER",
                    localizer,
                });
            },
        });
    }

    if (canInviteUsers) {
        otherItems.push({
            id: "invite-user",
            icon: ShieldPlus,
            requiresLogin: true,
            accountAccessDialog: accountAreaAccountAccessDialog,
            text: localizer("group.invitations.invite_user"),
            onClick: () => onInviteUser(),
        });
    }

    // Mutually exclusive with `invite-user` by construction: `resolveGroupInviteAffordance` returns one
    // value, so the menu can never offer both. That matters more than it looks — two controls whose
    // labels differ by one word ("Invite" / "Ask to invite") sitting together is precisely the
    // ambiguity rule 23 warns about.
    if (canRequestInvite) {
        otherItems.push({
            id: "request-invite",
            icon: ShieldPlus,
            requiresLogin: true,
            accountAccessDialog: accountAreaAccountAccessDialog,
            text: localizer("group.invite_request.request_invite"),
            onClick: () => showGroupInviteRequestDialog({groupPublicId, localizer}),
        });
    }

    // The invite LINK, for people who are not on Seavien yet — as opposed to `invite-user` directly
    // above, which invites an existing account by handle. Three gates, none of them new:
    //   • PUBLIC groups only. A link into a private group would be an authorization change, and this is
    //     deliberately just a deep link plus attribution (referral design §5A.2).
    //   • Members and leaders only, reusing `canInviteUsers` rather than inventing a second permission
    //     rule. A non-member still has the Share item below, which does the same thing minus the moment;
    //     an anonymous visitor gets neither, and correctly so — an anonymous share records
    //     `user_id = NULL` and therefore credits nobody.
    //   • The group PAGE only. `GroupButtons` also mounts on feed cards, the discovery rail and the
    //     welcome overlay, which are navigation moments rather than moments of belonging (§3.4's
    //     placement table). `group_page_header` is passed by exactly the two group-page headers.
    // Chat completeness D24 / D25 (§F18): a LEADER's link is a real join credential now (the leader's link is
    // the approval, private groups included), so a leader gets the create / copy / revoke panel instead. A
    // non-leader member of a PUBLIC group keeps the deep link below, which grants nothing and needs no row.
    const isAtLeastModerator = viewerLeadershipLevel === "OWNER"
        || viewerLeadershipLevel === "ADMINISTRATOR"
        || viewerLeadershipLevel === "MODERATOR";
    const canManageInviteLinks = isAtLeastModerator && Boolean(initiallyLeader) && !isBanned
        && surface === "group_page_header";
    if (canManageInviteLinks) {
        otherItems.push({
            id: "group-invite-link",
            icon: Link2,
            requiresLogin: true,
            accountAccessDialog: accountAreaAccountAccessDialog,
            text: localizer("group.invite_link.action"),
            onClick: () => void openGroupInviteLinkPanel({ groupPublicId, localizer }),
        });
    }
    const canInviteWithLink = !canManageInviteLinks && !privateAccess && canInviteUsers && surface === "group_page_header";
    if (canInviteWithLink) {
        otherItems.push({
            id: "group-invite-link",
            icon: Link2,
            requiresLogin: true,
            accountAccessDialog: accountAreaAccountAccessDialog,
            text: localizer("group.invite_link.action"),
            onClick: (ev?: Event) => onInviteWithLink(ev),
        });
    }

    otherItems.push({
        id: "share-group",
        icon: Share,
        requiresLogin: false,
        text: localizer("literal.share"),
        onClick: (ev?: Event) => onShareGroup(ev),
    });

    const canOrganizeLists = MetaHandler.isUserLoggedIn()
        && (isMember || Boolean(initiallyLeader))
        && !isBanned
        && Boolean(groupPublicId);
    if (canOrganizeLists) {
        otherItems.push(...buildGroupListMenuItems({ groupPublicId, lists: groupLists, localizer }));
    }

    if (isMember) {
        otherItems.push({
            id: "leave-group",
            icon: UserMinus,
            requiresLogin: true,
            accountAccessDialog: groupMembershipAccountAccessDialog,
            // `GROUP-126` (C18): for the OWNER this item starts a hand-over, so it says so; everyone else
            // reads the same "Leave this group" the confirm dialog and the room menu use.
            text: viewerLeadershipLevel === "OWNER"
                ? localizer("group.leave.owner")
                : localizer("chat.group.leave.action"),
            onClick: async (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                    // ignore
                }

                if (busy) {
                    toast.error(localizer("group.action_in_progress"));
                    return;
                }

                // Chat completeness A14 / D16 / Q6: a leader is no longer refused here. They get the ONE
                // leader way out (the room menu opens the same dialog): an owner chooses a new owner and is
                // never offered step-down; any other leader steps down, then leaves.
                if (cannotLeaveBecauseLeader) {
                    void openGroupLeaderLeaveDialog({
                        groupPublicId,
                        leadershipLevel: viewerLeadershipLevel,
                        privateAccess,
                        surface,
                        localizer,
                    });
                    return;
                }

                if (isBanned) {
                    toast.error(localizer("group.banned.cannot_join"));
                    return;
                }

                await toggleMembership();
            },
        });
    }

    // A leader who can already act on their own group's problems from the manage console has no use
    // for a platform report against it, and the report copy addresses a bystander. Gated the same way
    // edit-group and invite-user are: an action a viewer cannot sensibly complete is not offered.
    if (!canAdministerGroup) {
        moderationItems.push({
        id: "report-group",
        icon: Flag,
        requiresLogin: true,
        accountAccessDialog: reportAccountAccessDialog,
        destructive: true,
        text: localizer("report.modal.submit_button.entity", localizer("report.entity.group")),
        onClick: (ev?: Event) => {
            try {
                ev?.preventDefault();
                ev?.stopPropagation();
            } catch {
                // ignore
            }
            showReportEntityDialog(localizer, "group", groupPublicId);
        },
        });
    }

    const dropdownSections: DropdownSection[] = [];
    if (otherItems.length > 0) dropdownSections.push({id: "other", title: null, items: otherItems});
    if (moderationItems.length > 0) dropdownSections.push({id: "moderation", title: null, items: moderationItems});
    // `DISCUSSION-5`, operator decision `A` (2026-08-20, applied wave 8): filled primary is reserved for
    // Reply, so the join/follow actions a rail module draws are `outline`. `primary-only` is exactly the
    // rail/overlay presentation; the group page's own header (`compact` + `alignStart`) is not it and
    // keeps its filled Join, because there the primary action really does own the row. This moves in
    // lockstep with `SeriesButtons.followVariant` — split across two waves it would put an outline
    // Follow beside a filled Join inside one card.
    // `DISCOVERY-74` (2026-09-02) adds the third demotion axis, `demotePrimaryAction` — the same
    // extension `UserButtons.followVariant` already carries for `GROUP-20`, so the two families do not
    // drift. It is a no-op for every caller that existed before: the only host passing it was the
    // dashboard's self-view membership list, where the viewer is a member by construction and this
    // line already resolved to `outline`. What it newly reaches is `GroupCardDisplayer`'s anonymous
    // branch, where the card's primary action is READ. ⛔ B12 is untouched: the group page's own
    // header mounts this component directly and passes no `demotePrimaryAction`.
    const isRailPresentation = displayMode === "primary-only";
    // Round 38 D31: a primary-only mount may ask for the primary tier (the empty-group panel's one action).
    const railJoinIsQuiet = isRailPresentation && primaryOnlyTone !== "primary";
    const joinVariant = (isMember || railJoinIsQuiet || demotePrimaryAction) ? "outline" : "primary";
    // `ERRORS-11`: these two lines used to take OPPOSITE branches — joining a public group you can
    // already read was `primary`, while requesting access to one you cannot see, the single thing the
    // private-group wall exists to let you do, was `ghost`: no fill, no border, lighter than the plain
    // count chips 60px above it and indistinguishable from the `...` overflow trigger beside it.
    //
    // The row proposed keying this off a new `deniedAccess` prop instead, on the premise that the
    // non-rail, non-header mount wants `ghost`. It does not: the other non-rail mount is the group
    // CARD (directory, dashboard, profile lists), where the line above already draws a public group's
    // Join filled, so a `ghost` request there is the same defect one surface over rather than a case
    // worth protecting. `isRailPresentation` is the right axis precisely because it does NOT mean "in
    // a header" — it means "this is the rail/overlay presentation", which is exactly the scope
    // operator decision B12 reserved. Rails and the onboarding overlay therefore stay `outline`, and
    // `isRequestSent` stays `outline` because a settled state is not a call to action.
    // `DISCOVERY-74`: same axis as `joinVariant` above, for the same reason. A signed-out visitor
    // looking at a private group cannot complete a request either, so the two must not disagree —
    // that disagreement is precisely the defect `ERRORS-11` closed.
    const requestVariant = (isRequestSent || isRailPresentation || demotePrimaryAction) ? "outline" : "primary";

    // `compact` + `alignStart` is the group page's own header, where the primary action owns the row.
    const stretchPrimaryOnRow = stretchPrimary && compact;

    const joinButton = (
        <CtaToggle
            icon={isMember ? UserMinus : UserPlus}
            text={isMember ? (localizer("group.leave")) : (localizer("group.join"))}
            displayTextOnMobile={true}
            buttonActive={isMember}
            extraClass={cn("join-group-button min-w-0", stretchPrimaryOnRow && "w-full justify-center")}
            disabled={isBanned || busy || (isMember && cannotLeaveBecauseLeader)}
            requiresLogin
            accountAccessDialog={groupJoinAccountAccessDialog}
            size={compact ? "sm" : "md"}
            variant={joinVariant}
            playwrightId={joinPlaywrightId}
            aria-disabled={isBanned || busy || (isMember && cannotLeaveBecauseLeader)}
            onPressedChange={() => {
                if (!isBanned && !cannotLeaveBecauseLeader && !busy) {
                    return toggleMembership();
                }
            }}
        />
    );

    const requestButton = (
        <CtaButton
            icon={UserPlus}
            text={isRequestSent ? localizer("group.request_sent") : localizer("group.request_join")}
            displayTextOnMobile={true}
            buttonActive={false}
            extraClass={cn("request-join-button min-w-0", stretchPrimaryOnRow && "w-full justify-center")}
            disabled={isRequesting || isRequestSent}
            requiresLogin
            accountAccessDialog={groupJoinAccountAccessDialog}
            size={compact ? "sm" : "md"}
            variant={requestVariant}
            playwrightId="group-request-join"
            aria-disabled={isRequesting || isRequestSent}
            onClick={(e) => {
                e?.preventDefault();
                e?.stopPropagation();
                if (!isRequestSent && !isRequesting) {
                    void requestToJoin();
                }
            }}
        />
    );

    const joinDisabledReasonKey = isBanned
        ? "group.banned.cannot_join"
        : (isMember && cannotLeaveBecauseLeader)
            ? cannotLeaveReasonKey
            : (busy ? "group.action_in_progress" : null);

    const requestDisabledReasonKey = isRequestSent
        ? "group.request_already_sent"
        : (isRequesting ? "group.request_in_progress" : null);

    const shouldShowRequestOnly = privateAccess && !isMember && !isBanned;
    const shouldHidePrimaryAction = privateAccess && !isMember && isBanned && !viewerInvitation?.invitationPublicId;
    const addDiscussionButton = isMember ? (
        <CtaButton
            icon={PlusSquare}
            text={localizer("group.add.discussion")}
            // `DASHBOARD-62`: opted in by the card only. The `ariaLabel` is unconditional rather than
            // conditional on the collapse — the name is correct at every width, and a name that
            // appears and disappears with a media query is the defect one layer down.
            ariaLabel={localizer("group.add.discussion")}
            displayTextOnMobile={!collapsePrimaryLabelOnMobile}
            extraClass={cn("add-discussion-to-group-button min-w-0", stretchPrimaryOnRow && "w-full justify-center")}
            requiresLogin
            accountAccessDialog={addToGroupAccountAccessDialog}
            size={compact ? "sm" : "md"}
            // `DASHBOARD-12`: quiet on a surface whose own primary job is navigation. The host says so;
            // this component has no way to know, and guessing from `surface` would be an analytics
            // signal doing presentation work.
            variant={demotePrimaryAction ? "outline" : "primary"}
            playwrightId="group-add-discussion"
            onClick={(e) => {
                e?.preventDefault();
                e?.stopPropagation();
                onAddDiscussionToGroup();
            }}
        />
    ) : null;

    const invitationPrimaryAction = viewerInvitation?.invitationPublicId ? (
        <div className={cn("flex min-w-0 gap-2", compact ? "w-full flex-col" : "flex-wrap items-center justify-end")}>
            <CtaButton
                icon={UserCheck2}
                text={localizer("group.invitations.accept")}
                displayTextOnMobile={true}
                extraClass={cn("accept-group-invitation-button min-w-0", compact && "w-full")}
                requiresLogin
                accountAccessDialog={groupMembershipAccountAccessDialog}
                size={compact ? "sm" : "md"}
                variant="primary"
                playwrightId="group-invitation-accept"
                disabled={invitationBusy}
                onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    void acceptInvitation();
                }}
            />
            <CtaButton
                icon={UserX}
                text={localizer("group.invitations.decline")}
                displayTextOnMobile={true}
                extraClass={cn("decline-group-invitation-button min-w-0", compact && "w-full")}
                requiresLogin
                accountAccessDialog={groupMembershipAccountAccessDialog}
                size={compact ? "sm" : "md"}
                variant="outline"
                playwrightId="group-invitation-decline"
                disabled={invitationBusy}
                onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    void declineInvitation();
                }}
            />
        </div>
    ) : null;

    const primaryAction = invitationPrimaryAction
        ?? (shouldHidePrimaryAction ? null : (isMember ? addDiscussionButton : (shouldShowRequestOnly ? requestButton : joinButton)));
    const primaryActionDisabledReasonKey = invitationPrimaryAction || shouldHidePrimaryAction
        ? null
        : (isMember ? null : (shouldShowRequestOnly ? requestDisabledReasonKey : joinDisabledReasonKey));
    const showDropdown = displayMode !== "primary-only" && dropdownSections.length > 0;
    const containerClassName = displayMode === "primary-only"
        ? (primaryOnlyAlign === "center" ? "flex w-full max-w-full items-center justify-center" : "flex w-full max-w-full items-center justify-end")
        : alignStart
            ? "flex w-full max-w-full items-center"
            : (compact ? "ml-auto flex w-auto max-w-full items-center" : "ml-auto flex w-full max-w-full items-center justify-end");
    const actionsClassName = displayMode === "primary-only"
        ? (primaryOnlyAlign === "center" ? "flex w-full max-w-full flex-wrap items-center justify-center gap-2" : "flex w-full max-w-full flex-wrap items-center justify-end gap-2")
        : alignStart
            ? "flex w-full max-w-full flex-wrap items-center justify-start gap-2"
            : (compact ? "flex max-w-full flex-wrap items-center justify-end gap-2" : "flex w-full max-w-full flex-wrap items-center justify-end gap-2");

    return (
        <div className={containerClassName}>


            <div className={actionsClassName}>
                {primaryAction && primaryActionDisabledReasonKey ? (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className={cn("inline-flex shrink-0", stretchPrimaryOnRow && "flex w-full min-w-0 flex-1 shrink")}>
                                {primaryAction}
                            </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" sideOffset={4}>
                            {localizer(primaryActionDisabledReasonKey)}
                        </TooltipContent>
                    </Tooltip>
                ) : primaryAction ? (
                    <div className={cn("inline-flex shrink-0", (invitationPrimaryAction || stretchPrimaryOnRow) && "flex w-full min-w-0 flex-1 shrink")}>
                        {primaryAction}
                    </div>
                ) : null}

                {isMember && (
                    <GroupNotificationLevelControl
                        groupPublicId={groupPublicId}
                        localizer={localizer}
                        compact={compact}
                    />
                )}

                {showDropdown && (
                    <div className="inline-flex shrink-0">
                        <CtaButtonDropdown
                            icon={MoreHorizontal}
                            // An existing key, deliberately: this trigger holds share / invite / report
                            // and for an anonymous visitor it is one of the card's two controls, but a
                            // new label would be a 24-locale bill (AGENTS.md rule 11). `GROUP-6`.
                            ariaLabel={localizer("typescript.discussion.preview.publish_settings.more_options")}
                            playwrightId="group-more-options-button"
                            displayTextOnMobile={true}
                            // GROUP-100 (A5): outline, not ghost. On the private door this trigger is one
                            // of two controls and the only route to Share and Report, and a ghost icon
                            // read as a decorative mark beside the filled pill. No label (rule 11).
                            variant="outline"
                            size={compact ? "sm" : "md"}
                            extraClass="group-more-options-button min-w-0"
                            dropdownSections={dropdownSections}
                            onOpenChange={(nextOpen: boolean) => {
                                if (nextOpen && canOrganizeLists) {
                                    void ensureGroupLists().catch((error) => logger.warn("Failed to load group lists", error));
                                }
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

